import { Check } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { cn } from '@/shared/utils/utils';
import { describeStrings } from '../../describe-stage/utils/describe.strings';

export type StageId =
  | 'describe'
  | 'gather'
  | 'match-points'
  | 'deep-dive'
  | 'lessons';

type EnabledStageRoute =
  | '/interactions/debrief-v2/$id/describe'
  | '/interactions/debrief-v2/$id/gather'
  | '/interactions/debrief-v2/$id/match-points'
  | '/interactions/debrief-v2/$id/deep-dive'
  | '/interactions/debrief-v2/$id/lessons';

interface Stage {
  id: StageId;
  label: string;
  enabled: boolean;
  to?: EnabledStageRoute;
}

type StageStatus = 'done' | 'current' | 'upcoming';

interface DebriefV2ChromeProps {
  sessionId: string;
  activeStage: StageId;
}

interface StageStepProps {
  stage: Stage;
  stepNumber: number;
  status: StageStatus;
  sessionId: string;
}

const STAGES: ReadonlyArray<Stage> = [
  {
    id: 'describe',
    label: describeStrings.pageHeader.stageDescribe,
    enabled: true,
    to: '/interactions/debrief-v2/$id/describe',
  },
  {
    id: 'gather',
    label: describeStrings.pageHeader.stageGatherFacts,
    enabled: true,
    to: '/interactions/debrief-v2/$id/gather',
  },
  {
    id: 'match-points',
    label: describeStrings.pageHeader.stageMatchPoints,
    enabled: true,
    to: '/interactions/debrief-v2/$id/match-points',
  },
  {
    id: 'deep-dive',
    label: describeStrings.pageHeader.stageDeepDive,
    enabled: true,
    to: '/interactions/debrief-v2/$id/deep-dive',
  },
  {
    id: 'lessons',
    label: describeStrings.pageHeader.stageLessons,
    enabled: true,
    to: '/interactions/debrief-v2/$id/lessons',
  },
];

function getCircleClass(status: StageStatus): string {
  if (status === 'done') return 'bg-emerald-500 text-white shadow-sm ring-0';
  if (status === 'current')
    return 'bg-primary text-white shadow-sm ring-4 ring-primary/15 w-10 h-10 text-[14px]';
  return 'bg-white text-slate-400 border border-slate-300';
}

function getLabelClass(status: StageStatus): string {
  if (status === 'current') return 'text-slate-900 font-semibold';
  if (status === 'done') return 'text-slate-600 font-medium';
  return 'text-slate-400 font-medium';
}

function StageStep({ stage, stepNumber, status, sessionId }: StageStepProps) {
  const circleClassName = cn(
    'flex items-center justify-center rounded-full text-[13px] font-semibold transition-all w-9 h-9',
    getCircleClass(status)
  );
  const labelClassName = cn(
    'text-[12px] tracking-tight transition-colors',
    getLabelClass(status)
  );

  const content = (
    <>
      <span className={circleClassName} aria-hidden>
        {status === 'done' ? (
          <Check className="h-[18px] w-[18px]" strokeWidth={3} />
        ) : (
          stepNumber
        )}
      </span>
      <span className={labelClassName}>{stage.label}</span>
    </>
  );

  const wrapperClassName =
    'flex flex-col items-center gap-1.5 group focus:outline-none';

  if (stage.enabled && stage.to) {
    return (
      <Link
        to={stage.to}
        params={{ id: sessionId }}
        aria-current={status === 'current' ? 'step' : undefined}
        className={wrapperClassName}
      >
        {content}
      </Link>
    );
  }

  return (
    <button
      type="button"
      disabled
      title="Coming soon"
      className={cn(wrapperClassName, 'cursor-not-allowed')}
    >
      {content}
    </button>
  );
}

function StageConnector({ filled }: { filled: boolean }) {
  return (
    <span
      className={cn(
        'h-[2px] w-14 mb-6 rounded-full transition-colors',
        filled ? 'bg-emerald-400' : 'bg-slate-200'
      )}
      aria-hidden
    />
  );
}

export function DebriefV2Chrome({
  sessionId,
  activeStage,
}: DebriefV2ChromeProps) {
  const activeIndex = STAGES.findIndex((stage) => stage.id === activeStage);

  return (
    <header className="sticky top-0 z-40 bg-page-bg/80 backdrop-blur-md flex items-center justify-center px-6 pt-14 pb-7">
      <ol className="hidden md:flex items-center">
        {STAGES.map((stage, index) => {
          let status: StageStatus = 'upcoming';
          if (index < activeIndex) status = 'done';
          else if (index === activeIndex) status = 'current';
          return (
            <li key={stage.id} className="flex items-center">
              <StageStep
                stage={stage}
                stepNumber={index + 1}
                status={status}
                sessionId={sessionId}
              />
              {index < STAGES.length - 1 && (
                <StageConnector filled={index < activeIndex} />
              )}
            </li>
          );
        })}
      </ol>
    </header>
  );
}
